import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import {
  PermissionAction,
  PermissionPreset,
  PermissionScreenNode,
  PermissionTreeNode,
} from '@app/features/dashboard/group-register/permission-tree/permission-tree.models';
import {
  countSelected,
  describeScreenSelection,
  isReadOnlySelection,
  screensOf,
} from '@app/features/dashboard/group-register/permission-tree/permission-selection.utils';
import { PermissionActionRowComponent } from '@app/features/dashboard/group-register/components/permission-action-row/permission-action-row.component';

export interface PermissionValueChange {
  value: string;
  checked: boolean;
}

export interface PermissionPresetRequest {
  node: PermissionTreeNode;
  preset: PermissionPreset;
}

/**
 * Painel da direita: a tela escolhida na árvore, com "Aparece no menu" e uma chave por ação separadas em Leitura e
 * Gravação. Com uma área ou um módulo escolhido, mostra as telas dele com a contagem e os mesmos atalhos
 * (Só leitura, Tudo, Nada) valendo para todas.
 */
@Component({
  selector: 'app-permission-screen-panel',
  standalone: true,
  imports: [SharedModule, PermissionActionRowComponent],
  templateUrl: './permission-screen-panel.component.html',
  styleUrls: ['./permission-screen-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionScreenPanelComponent {
  readonly node = input.required<PermissionTreeNode>();
  readonly selected = input.required<ReadonlySet<string>>();
  readonly initial = input<ReadonlySet<string>>(new Set());

  readonly valueChange = output<PermissionValueChange>();
  readonly presetApply = output<PermissionPresetRequest>();
  readonly screenOpen = output<PermissionScreenNode>();

  readonly screen = computed(() => {
    const node = this.node();
    return node.kind === 'screen' ? node : null;
  });

  readonly breadcrumb = computed(() => {
    const node = this.node();
    return node.kind === 'module' ? '' : node.path.join(' › ');
  });

  readonly childScreens = computed(() => screensOf(this.node()));

  readonly count = computed(() => countSelected(this.node().values, this.selected()));

  readonly subtitle = computed(() => {
    const { selected, total } = this.count();
    const screen = this.screen();
    const base = `${selected} de ${total} ${selected === 1 ? 'ligada' : 'ligadas'}`;
    if (screen) {
      const detail = describeScreenSelection(screen, this.selected());
      return detail ? `${base} · ${detail}` : base;
    }
    const screens = this.childScreens().length;
    return `${base} · ${screens} ${screens === 1 ? 'tela' : 'telas'}`;
  });

  /** O nó está em "só leitura" de fato (selo ao lado da contagem). */
  readonly isReadOnly = computed(() => isReadOnlySelection(this.node(), this.selected()));

  /** A tela junta mais de um grupo: cada ação mostra de qual grupo é. */
  readonly showGroups = computed(() => (this.screen()?.groupNames.length ?? 0) > 1);

  readonly readCount = computed(() =>
    countSelected(this.screen()?.readActions.map(action => action.value) ?? [], this.selected())
  );

  readonly writeCount = computed(() =>
    countSelected(this.screen()?.writeActions.map(action => action.value) ?? [], this.selected())
  );

  readonly presetScope = computed(() => {
    const kind = this.node().kind;
    return kind === 'screen' ? 'desta tela' : kind === 'area' ? 'desta área' : 'deste módulo';
  });

  isChecked(action: PermissionAction): boolean {
    return this.selected().has(action.value);
  }

  isChanged(action: PermissionAction): boolean {
    return this.selected().has(action.value) !== this.initial().has(action.value);
  }

  screenCount(screen: PermissionScreenNode) {
    return countSelected(screen.values, this.selected());
  }

  screenDetail(screen: PermissionScreenNode): string {
    return describeScreenSelection(screen, this.selected());
  }

  isMenuOn(screen: PermissionScreenNode): boolean {
    return !!screen.menuAction && this.selected().has(screen.menuAction.value);
  }

  apply(preset: PermissionPreset): void {
    this.presetApply.emit({ node: this.node(), preset });
  }

  toggle(action: PermissionAction, checked: boolean): void {
    this.valueChange.emit({ value: action.value, checked });
  }
}
